import ElementPreviewFactory from '@/components/activities/elements/preview/ElementPreviewFactory';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { useBankFilters } from '@/data/context/BankFiltersContext';
import useAuth from '@/data/hook/useAuth';
import { updateActivityInBank } from '@/data/services/_deprecated/activityServices';
import { isSuperAdmin } from '@/functions/auth';
import Activity from '@/models/_deprecated/Activity';
import { fadeIn } from '@/utils/animations/commom';
import { ArrowDownOnSquareIcon } from '@heroicons/react/24/outline';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useItems } from '../ListBankCards.hooks';
import { ExpansibleCardProps } from './ExpansibleCardProps';
import { useQuery } from '@tanstack/react-query';
import { correctAnswersQueryKeys } from '@/data/services/querykeys';
import { LoadingIcon } from '@/components/icons';

export default function ExpansibleQuestionCard({
  item: activity,
  used,
  onUse,
  loading,
  selectedId,
}: ExpansibleCardProps<Activity>) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'modalBank.useButton',
  });

  const question = activity.questions[0];

  const { data: answer, isInitialLoading: isLoadingAnswer } = useQuery({
    ...correctAnswersQueryKeys.get(activity.id),
    enabled: !!activity,
  });

  const questionWithAnswer = {
    ...question,
    answer: answer?.questions?.[0],
  };

  return (
    <div className="mt-4 flex flex-col gap-4">
      <ConditionalRenderer condition={isLoadingAnswer}>
        <div className="bg-base-100 px-2 py-4 rounded-lg flex items-center justify-center min-h-52">
          <LoadingIcon className="w-10 h-10 text-primary" />
        </div>
      </ConditionalRenderer>
      <ConditionalRenderer condition={!isLoadingAnswer}>
        <div className="bg-base-100 px-2 py-4 rounded-lg">
          <ElementPreviewFactory
            activityMode="answerkey"
            isAdmin
            hiddenStudents
            activityId={activity.id}
            {...{
              ...questionWithAnswer,
              isDone: true,
              progressId: 0,
              questionId: question.id,
              isCurrentQuestion: false,
            }}
          />
        </div>
      </ConditionalRenderer>
      <div className="flex gap-4 items-center flex-wrap">
        <ConditionalRenderer condition={!!onUse && !used}>
          <MainButton
            loading={loading || selectedId === activity.id}
            text={t('questions')}
            onClick={() => onUse?.(activity)}
            icon={<ArrowDownOnSquareIcon className="w-4" />}
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={loading || selectedId === activity.id}>
          <motion.div {...fadeIn}>
            <Text
              text={t('addingActivity')}
              format="rubik-500"
              className="text-primary animate-bounce"
            />
          </motion.div>
        </ConditionalRenderer>
        <QuestionPublicChange activity={activity} />
      </div>
    </div>
  );
}

type PublicChangeProps = {
  activity: Activity;
};
const QuestionPublicChange = ({ activity }: PublicChangeProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'lessonForm',
  });
  const { user } = useAuth();
  const { filters } = useBankFilters();
  const { invalidate: questionQueryInvalidate } = useItems(
    'questions',
    filters,
    {
      enabled: false,
    },
  );

  const isSuper = isSuperAdmin(user?.userType);

  const onSetPublic = async (isPublicInBank: boolean) => {
    await updateActivityInBank({
      activityId: activity.id,
      bank: { isPublicInBank, inBank: activity.inBank },
    });
    await questionQueryInvalidate();
  };

  return (
    <ConditionalRenderer condition={isSuper && activity.inBank}>
      <div className="flex gap-2 items-center justify-start">
        <input
          type="checkbox"
          className="toggle toggle-sm toggle-primary"
          checked={activity.isPublicInBank}
          onChange={e => onSetPublic(e.target.checked)}
        />
        <Text text={t('isPublicInBank')} className="flex" />
      </div>
    </ConditionalRenderer>
  );
};
